export type ApprovalDetailRow = { key: string; label: string; value: string }

export type ApprovalDetail = {
  destructive: boolean
  title?: string
  description?: string
  severity?: string
  confidence?: number
  confidenceBasis?: string
  policyId?: string
  expiresAt?: string
  whatWillHappen?: string[]
  rows: ApprovalDetailRow[]
}

const WRAPPER_ONLY_KEYS = new Set([
  'type',
  'tool',
  'destructive',
  'description',
  'kind',
  'title',
  'changes',
  'preview',
  'app_type',
  'evidence',
  'requires',
  'severity',
  'policy_id',
  'expires_at',
  'self_assessment',
  'what_will_happen'
])

const ALWAYS_HIDDEN_KEYS = new Set(['content_length', 'result_length'])

const LABEL_OVERRIDES: Record<string, string> = {
  content_preview: 'Content',
  content: 'Content',
  body_preview: 'Body',
  body: 'Body',
  old_preview: 'Before',
  old_text: 'Before',
  new_preview: 'After',
  new_text: 'After',
  has_html: 'Includes HTML',
  to: 'To',
  cc: 'Cc',
  from: 'From'
}

const humaniseLabel = (key: string): string =>
  LABEL_OVERRIDES[key] ??
  key
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())

const MAX_ARRAY_ITEMS = 5
const MAX_STRING_CHARS = 300

const truncate = (s: string): string =>
  s.length > MAX_STRING_CHARS ? `${s.slice(0, MAX_STRING_CHARS)}…` : s

const readString = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() !== '' ? v : undefined

const readNumber = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined

const readObject = (v: unknown): Record<string, unknown> | undefined =>
  v != null && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : undefined

const readStringArray = (v: unknown): string[] | undefined => {
  if (!Array.isArray(v)) return undefined
  const items = v
    .map((x) => (typeof x === 'string' ? x.trim() : ''))
    .filter((x) => x !== '')

  return items.length > 0 ? items : undefined
}

const formatValue = (value: unknown): string | undefined => {
  if (value == null) return undefined
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'string')
    return value === '' ? undefined : truncate(value)
  if (Array.isArray(value)) {
    if (value.length === 0) return undefined
    const shown = value
      .slice(0, MAX_ARRAY_ITEMS)
      .map((v) => truncate(String(v)))
    const extra = value.length - shown.length

    return extra > 0 ? `${shown.join(', ')}, +${extra} more` : shown.join(', ')
  }
  if (typeof value === 'object') {
    try {
      return truncate(JSON.stringify(value))
    } catch {
      return truncate(String(value))
    }
  }

  return String(value)
}

export const buildApprovalDetail = (
  value: Record<string, unknown> | undefined
): ApprovalDetail => {
  if (!value) return { destructive: false, rows: [] }

  const destructive = Boolean(value.destructive)

  const hasWrappedArgs =
    value.args != null &&
    typeof value.args === 'object' &&
    !Array.isArray(value.args)
  const fields = (hasWrappedArgs ? value.args : value) as Record<
    string,
    unknown
  >

  const rows: ApprovalDetailRow[] = []
  for (const [key, raw] of Object.entries(fields)) {
    if (!hasWrappedArgs && WRAPPER_ONLY_KEYS.has(key)) continue
    if (ALWAYS_HIDDEN_KEYS.has(key)) continue
    const formatted = formatValue(raw)
    if (formatted === undefined) continue
    rows.push({ key, label: humaniseLabel(key), value: formatted })
  }

  const detail: ApprovalDetail = { destructive, rows }
  const selfAssessment = readObject(value.self_assessment)

  const title = readString(value.title)
  if (title) detail.title = title

  const description = readString(value.description)
  if (description) detail.description = description

  const severity = readString(value.severity)
  if (severity) detail.severity = severity.toLowerCase()

  const confidence = readNumber(selfAssessment?.confidence)
  if (confidence !== undefined) detail.confidence = confidence

  const confidenceBasis = readString(selfAssessment?.confidence_basis)
  if (confidenceBasis) detail.confidenceBasis = confidenceBasis

  const policyId = readString(value.policy_id)
  if (policyId) detail.policyId = policyId

  const expiresAt = readString(value.expires_at)
  if (expiresAt) detail.expiresAt = expiresAt

  const whatWillHappen = readStringArray(value.what_will_happen)
  if (whatWillHappen) detail.whatWillHappen = whatWillHappen

  return detail
}
